import React, { useState, useRef } from 'react';
import { Shield, AlertTriangle, Scale, FileText, ChevronDown, ChevronUp, Send, Loader2, Info, TrendingUp, TrendingDown, Minus, CheckCircle2, HelpCircle, Search, ShieldCheck, ListChecks } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* -- P77: Safer Discipline Prediction panel --
   Primary visual treatment for the new `discipline_prediction` field.
   Renders ABOVE the legacy risk gauge so the steward sees the
   stage/category first and treats the raw risk_score as a secondary signal.
*/
const CATEGORY_TONES = {
  no_discipline_indicated:         { color: '#22c55e', bg: 'rgba(34,197,94,0.10)',  border: 'rgba(34,197,94,0.40)',  pill: 'NO DISCIPLINE INDICATED' },
  fact_finding_investigation_risk: { color: '#7dd3fc', bg: 'rgba(125,211,252,0.10)', border: 'rgba(125,211,252,0.40)', pill: 'FACT-FINDING / INVESTIGATION' },
  coaching_counseling_risk:        { color: '#22c55e', bg: 'rgba(34,197,94,0.08)',  border: 'rgba(34,197,94,0.30)',  pill: 'COACHING / COUNSELING' },
  written_warning_risk:            { color: '#facc15', bg: 'rgba(250,204,21,0.10)',  border: 'rgba(250,204,21,0.45)',  pill: 'WRITTEN WARNING' },
  suspension_risk:                 { color: '#fb923c', bg: 'rgba(251,146,60,0.12)',  border: 'rgba(251,146,60,0.50)',  pill: 'SUSPENSION' },
  termination_risk:                { color: '#ef4444', bg: 'rgba(239,68,68,0.14)',   border: 'rgba(239,68,68,0.55)',   pill: 'TERMINATION RISK' },
};

const STAGE_LABEL = {
  general_question:   'General / hypothetical question',
  investigation_only: 'Investigation only (no discipline issued)',
  already_issued:    'Discipline already issued',
  no_indicator:      'No stage indicator from facts',
};

const CONFIDENCE_TONES = {
  high:   { color: '#ef4444', label: 'HIGH'   },
  medium: { color: '#facc15', label: 'MEDIUM' },
  low:    { color: '#94a3b8', label: 'LOW'    },
};

function SaferDisciplinePredictionPanel({ prediction }) {
  if (!prediction || typeof prediction !== 'object') return null;
  const tone = CATEGORY_TONES[prediction.category] || CATEGORY_TONES.no_discipline_indicated;
  const conf = CONFIDENCE_TONES[prediction.confidence] || CONFIDENCE_TONES.low;
  const mit  = prediction.mitigating_circumstances || {};
  const mentioned = mit.mentioned || [];
  const missing   = mit.missing_to_check || [];

  return (
    <div
      className="cp-safer"
      data-testid="safer-discipline-prediction"
      data-category={prediction.category}
      data-stage={prediction.stage}
      data-confidence={prediction.confidence}
      style={{ background: tone.bg, borderColor: tone.border }}
    >
      {/* Header */}
      <div className="cp-safer-head">
        <span className="cp-safer-pill" style={{ background: tone.color + '22', color: tone.color, borderColor: tone.color + '88' }}
              data-testid="safer-category-pill">
          <ShieldCheck size={14} /> {tone.pill}
        </span>
        <span className="cp-safer-stage" data-testid="safer-stage-label">
          Stage: {STAGE_LABEL[prediction.stage] || prediction.stage}
        </span>
        <span className="cp-safer-confidence" data-testid="safer-confidence-pill"
              style={{ borderColor: conf.color + '88', color: conf.color }}>
          Confidence: {conf.label}
        </span>
      </div>

      {/* Headline */}
      <p className="cp-safer-headline" data-testid="safer-headline">
        {prediction.headline}
      </p>

      {/* Termination-tier signals (only when present) */}
      {prediction.termination_tier_signals?.length > 0 && (
        <div className="cp-safer-signals" data-testid="safer-termination-signals">
          <AlertTriangle size={13} /> Termination-tier conduct cues found:{' '}
          {prediction.termination_tier_signals.slice(0, 3).map((s, i) => (
            <code key={i}>{s}</code>
          ))}
        </div>
      )}

      {/* Always-ask block */}
      <div className="cp-safer-ask" data-testid="safer-always-ask">
        <div className="cp-safer-ask-head"><HelpCircle size={13} /> ALWAYS ASK</div>
        <ul>
          <li data-testid="safer-ask-weingarten">
            {prediction.always_ask?.weingarten_requested}
          </li>
          <li data-testid="safer-ask-discipline-status">
            {prediction.always_ask?.discipline_status}
          </li>
        </ul>
      </div>

      {/* 3-column: supporting / missing facts / safer action */}
      <div className="cp-safer-grid">
        <div className="cp-safer-col" data-testid="safer-supporting-facts">
          <div className="cp-safer-col-head"><Search size={12} /> SUPPORTING FACTS</div>
          {prediction.supporting_facts?.length > 0 ? (
            <ul>
              {prediction.supporting_facts.map((s, i) => (
                <li key={i} data-testid={`safer-supporting-fact-${i}`}>{s}</li>
              ))}
            </ul>
          ) : (
            <p className="cp-safer-empty">—</p>
          )}
        </div>
        <div className="cp-safer-col" data-testid="safer-missing-facts">
          <div className="cp-safer-col-head"><HelpCircle size={12} /> MISSING / TO CONFIRM</div>
          <ul>
            {(prediction.missing_facts || []).map((s, i) => (
              <li key={i} data-testid={`safer-missing-fact-${i}`}>{s}</li>
            ))}
          </ul>
        </div>
        <div className="cp-safer-col" data-testid="safer-recommended-action-col">
          <div className="cp-safer-col-head"><Scale size={12} /> SAFER ACTION</div>
          <p data-testid="safer-recommended-action">
            {prediction.safer_recommended_action}
          </p>
        </div>
      </div>

      {/* Mitigating circumstances */}
      <div className="cp-safer-mit" data-testid="safer-mitigating">
        <div className="cp-safer-mit-head"><ListChecks size={13} /> MITIGATING CIRCUMSTANCES</div>
        <div className="cp-safer-mit-grid">
          <div data-testid="safer-mit-mentioned">
            <div className="cp-safer-mit-col-head">
              <CheckCircle2 size={12} style={{ color: '#22c55e' }} /> Already mentioned ({mentioned.length})
            </div>
            {mentioned.length === 0 ? (
              <p className="cp-safer-empty">None mentioned yet — ask about each in the next column.</p>
            ) : (
              <ul>
                {mentioned.map((m, i) => (
                  <li key={m.key} data-testid={`safer-mit-mentioned-${m.key}`}>{m.label}</li>
                ))}
              </ul>
            )}
          </div>
          <div data-testid="safer-mit-missing">
            <div className="cp-safer-mit-col-head">
              <HelpCircle size={12} style={{ color: '#facc15' }} /> Steward must still check ({missing.length})
            </div>
            <ul>
              {missing.map((m, i) => (
                <li key={m.key} data-testid={`safer-mit-missing-${m.key}`}>{m.label}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

function RiskGauge({ score }) {
  const getColor = () => {
    if (score >= 80) return '#ef4444';
    if (score >= 60) return '#f97316';
    if (score >= 40) return '#eab308';
    if (score >= 20) return '#22c55e';
    return '#6b7280';
  };
  const getLabel = () => {
    if (score >= 80) return 'CRITICAL';
    if (score >= 60) return 'HIGH';
    if (score >= 40) return 'MODERATE';
    if (score >= 20) return 'LOW';
    return 'MINIMAL';
  };
  const rotation = -90 + (score / 100) * 180;
  return (
    <div className="cp-gauge" data-testid="risk-gauge">
      <svg viewBox="0 0 200 120" width="200" height="120">
        <defs>
          <linearGradient id="gaugeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22c55e" />
            <stop offset="40%" stopColor="#eab308" />
            <stop offset="70%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="14" strokeLinecap="round" />
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="url(#gaugeGrad)" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${(score / 100) * 251.2} 251.2`} />
        <line x1="100" y1="100" x2="100" y2="30" stroke={getColor()} strokeWidth="3" strokeLinecap="round"
          transform={`rotate(${rotation} 100 100)`} style={{ transition: 'transform 0.8s ease-out' }} />
        <circle cx="100" cy="100" r="6" fill={getColor()} />
      </svg>
      <div className="cp-gauge-score" style={{ color: getColor() }} data-testid="risk-score-value">{score}</div>
      <div className="cp-gauge-label" style={{ color: getColor() }} data-testid="risk-level-label">{getLabel()}</div>
    </div>
  );
}

function Section({ icon: Icon, title, children, accent = 'blue', defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  const colors = {
    red: 'rgba(239,68,68,0.15)', orange: 'rgba(249,115,22,0.15)',
    yellow: 'rgba(234,179,8,0.15)', green: 'rgba(34,197,94,0.15)',
    blue: 'rgba(59,130,246,0.15)', violet: 'rgba(139,92,246,0.15)',
  };
  const textColors = {
    red: '#fca5a5', orange: '#fdba74', yellow: '#fde047',
    green: '#86efac', blue: '#93c5fd', violet: '#c4b5fd',
  };
  return (
    <div className="cp-section" data-testid={testId}>
      <button className="cp-section-head" onClick={() => setOpen(!open)}>
        <div className="cp-section-icon" style={{ background: colors[accent] }}>
          <Icon size={14} style={{ color: textColors[accent] }} />
        </div>
        <span className="cp-section-title">{title}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="cp-section-body">{children}</div>}
    </div>
  );
}

function MatchedRuleCard({ rule }) {
  const weightColors = { critical: '#ef4444', high: '#f97316', moderate: '#eab308', low: '#22c55e' };
  const isSemanticOnly = rule.match_type === 'semantic';
  return (
    <div className="cp-rule-card" data-testid={`rule-${rule.id}`}>
      <div className="cp-rule-header">
        <span className="cp-rule-issue">{rule.issue}</span>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          {isSemanticOnly && <span className="cp-match-badge cp-match-semantic">SEMANTIC</span>}
          {!isSemanticOnly && rule.keyword_hits > 0 && <span className="cp-match-badge cp-match-keyword">KEYWORD</span>}
          <span className="cp-rule-weight" style={{ color: weightColors[rule.policy_weight] || '#9ca3af' }}>
            {rule.policy_weight?.toUpperCase()}
          </span>
        </div>
      </div>
      <div className="cp-rule-meta">
        <span>Base Score: {rule.base_score}</span>
        <span>Category: {rule.category}</span>
        {rule.keyword_hits > 0 && <span>Hits: {rule.keyword_hits}</span>}
        {rule.similarity > 0 && <span>Similarity: {(rule.similarity * 100).toFixed(0)}%</span>}
      </div>
      <div className="cp-rule-outcome">{rule.likely_outcome}</div>
      {rule.matched_keywords?.length > 0 && (
        <div className="cp-rule-keywords">
          {rule.matched_keywords.map((kw, i) => <span key={i} className="cp-kw-tag">{kw}</span>)}
        </div>
      )}
    </div>
  );
}

function ScenarioCard({ scenario }) {
  const riskColors = { critical: '#ef4444', high: '#f97316', moderate: '#eab308', low: '#22c55e' };
  return (
    <div className="cp-scenario-card" data-testid={`scenario-${scenario.id}`}>
      <div className="cp-scenario-header">
        <span className="cp-scenario-cat">{scenario.category}</span>
        <span className="cp-scenario-risk" style={{ color: riskColors[scenario.risk_level] }}>
          {scenario.risk_level?.toUpperCase()}
        </span>
      </div>
      <p className="cp-scenario-text">{scenario.scenario_text}</p>
      <div className="cp-scenario-answer">{scenario.policy_answer}</div>
    </div>
  );
}

function PolicyCard({ policy }) {
  return (
    <div className="cp-policy-card" data-testid={`policy-${policy.id}`}>
      <div className="cp-policy-title">{policy.title}</div>
      <p className="cp-policy-summary">{policy.summary}</p>
      <div className="cp-policy-source">{policy.source_section}</div>
    </div>
  );
}

export default function ConductPredictorPage() {
  const [input, setInput] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  async function analyze() {
    if (!input.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/askstewie/conduct/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userInput: input }),
      });
      if (!res.ok) throw new Error(`API error ${res.status}`);
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      analyze();
    }
  }

  return (
    <div className="cp-page" data-testid="conduct-predictor-page">
      <div className="cp-container">
        {/* Header */}
        <div className="cp-header">
          <div className="cp-header-icon"><Shield size={24} /></div>
          <div>
            <h1 className="cp-title" data-testid="page-title">Discipline Risk Predictor</h1>
            <p className="cp-subtitle">Policy-grounded conduct risk analysis based on the Exelon Code of Business Conduct</p>
          </div>
        </div>

        {/* Input */}
        <div className="cp-input-area" data-testid="input-area">
          <textarea
            ref={textareaRef}
            data-testid="conduct-input"
            className="cp-textarea"
            placeholder="Describe the situation. Example: 'I showed up late and my boss said I smelled like alcohol. They want to drug test me but I refused.'"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
          />
          <button
            data-testid="analyze-button"
            className="cp-analyze-btn"
            onClick={analyze}
            disabled={loading || !input.trim()}
          >
            {loading ? <Loader2 size={18} className="cp-spin" /> : <Send size={18} />}
            <span>{loading ? 'Analyzing...' : 'Analyze Risk'}</span>
          </button>
        </div>

        {/* P85: Starter chips — guide new users on what to type */}
        {!input.trim() && !loading && (
          <div className="cp-starters" data-testid="conduct-starters">
            <span className="cp-starters-label">TRY ONE OF THESE</span>
            <div className="cp-starters-row">
              {[
                'I tested positive for cannabis',
                'I refused a drug test',
                'I got into an argument with my supervisor',
                'I was 30 minutes late twice this week',
                'I was accused of horseplay on the job',
                'They want to fact-find me about an incident',
              ].map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className="cp-starter-chip"
                  data-testid={`conduct-starter-${i}`}
                  onClick={() => setInput(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="cp-error" data-testid="error-message">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {/* Results */}
        {result && (
          <div className="cp-results" data-testid="analysis-results">
            {/* P77: Safer Discipline Prediction (primary signal) */}
            <SaferDisciplinePredictionPanel prediction={result.discipline_prediction} />

            {/* Legacy gauge + outcome (preserved for backward compat / drill-down) */}
            <div className="cp-top-row">
              <RiskGauge score={result.risk_score} />
              <div className="cp-outcome-panel">
                <h2 className="cp-outcome-title" data-testid="likely-outcome">{result.likely_outcome}</h2>
                <p className="cp-outcome-desc" data-testid="what-that-means">{result.what_that_means}</p>
              </div>
            </div>

            {/* Recommended Action */}
            <div className="cp-action-box" data-testid="recommended-action">
              <div className="cp-action-header">
                <Scale size={16} /> RECOMMENDED ACTION
              </div>
              <p>{result.recommended_action}</p>
            </div>

            {/* Score Breakdown */}
            {result.score_breakdown && (
              <div className="cp-breakdown" data-testid="score-breakdown">
                <div className="cp-bd-item">
                  <span>Base Score</span><span>{result.score_breakdown.base_score}</span>
                </div>
                {result.score_breakdown.semantic_boost > 0 && (
                  <div className="cp-bd-item cp-bd-sem">
                    <span><Info size={12} /> Semantic Boost</span><span>+{result.score_breakdown.semantic_boost}</span>
                  </div>
                )}
                {result.detected_aggravators?.length > 0 && (
                  <div className="cp-bd-item cp-bd-agg">
                    <span><TrendingUp size={12} /> Aggravators</span><span>+{result.score_breakdown.aggravator_total}</span>
                  </div>
                )}
                {result.detected_mitigators?.length > 0 && (
                  <div className="cp-bd-item cp-bd-mit">
                    <span><TrendingDown size={12} /> Mitigators</span><span>{result.score_breakdown.mitigator_total}</span>
                  </div>
                )}
                {result.detected_intent && (
                  <div className="cp-bd-item cp-bd-int">
                    <span><Minus size={12} /> Intent ({result.detected_intent.label})</span>
                    <span>{result.detected_intent.score_modifier >= 0 ? '+' : ''}{result.detected_intent.score_modifier}</span>
                  </div>
                )}
                <div className="cp-bd-item cp-bd-total">
                  <span>Final Risk Score</span><span>{result.risk_score}/100</span>
                </div>
                {result.confidence != null && (
                  <div className="cp-bd-item" style={{ borderBottom: 'none' }}>
                    <span>Match Confidence</span>
                    <span data-testid="confidence-value" style={{ color: result.confidence >= 0.6 ? '#4ade80' : result.confidence >= 0.3 ? '#facc15' : '#f87171' }}>
                      {(result.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Aggravators & Mitigators */}
            {(result.detected_aggravators?.length > 0 || result.detected_mitigators?.length > 0) && (
              <div className="cp-factors">
                {result.detected_aggravators?.map(a => (
                  <span key={a.id} className="cp-factor cp-factor-agg" data-testid={`aggravator-${a.id}`}>
                    <TrendingUp size={12} /> {a.label} (+{a.weight})
                  </span>
                ))}
                {result.detected_mitigators?.map(m => (
                  <span key={m.id} className="cp-factor cp-factor-mit" data-testid={`mitigator-${m.id}`}>
                    <TrendingDown size={12} /> {m.label} ({m.weight})
                  </span>
                ))}
              </div>
            )}

            {/* Matched Rules */}
            {result.matched_rules?.length > 0 && (
              <Section icon={AlertTriangle} title={`MATCHED RULES (${result.matched_rules.length})`} accent="red" testId="section-matched-rules">
                {result.matched_rules.map(r => <MatchedRuleCard key={r.id} rule={r} />)}
              </Section>
            )}

            {/* Matched Scenarios */}
            {result.matched_scenarios?.length > 0 && (
              <Section icon={FileText} title={`SIMILAR SCENARIOS (${result.matched_scenarios.length})`} accent="orange" testId="section-matched-scenarios">
                {result.matched_scenarios.map(s => <ScenarioCard key={s.id} scenario={s} />)}
              </Section>
            )}

            {/* Policy Support */}
            {result.policy_support?.length > 0 && (
              <Section icon={Scale} title={`POLICY SUPPORT (${result.policy_support.length})`} accent="blue" testId="section-policy-support">
                {result.policy_support.map(p => <PolicyCard key={p.id} policy={p} />)}
              </Section>
            )}

            {/* Disclaimer */}
            <div className="cp-disclaimer" data-testid="disclaimer">
              <Info size={14} />
              <span>{result.disclaimer}</span>
            </div>
          </div>
        )}
      </div>

      <style>{`
.cp-page {
  min-height: 100vh;
  background: #0a0a0f;
  color: #e5e5e5;
  padding: 24px 16px;
}
.cp-container {
  max-width: 780px;
  margin: 0 auto;
}
.cp-header {
  display: flex; align-items: center; gap: 16px;
  margin-bottom: 28px;
}
.cp-header-icon {
  width: 48px; height: 48px; border-radius: 14px;
  background: rgba(239,68,68,0.1); border: 1px solid rgba(239,68,68,0.2);
  display: flex; align-items: center; justify-content: center;
  color: #f87171; flex-shrink: 0;
}
.cp-title {
  font-size: 22px; font-weight: 700; color: #fafafa; margin: 0;
  letter-spacing: -0.02em;
}
.cp-subtitle {
  font-size: 13px; color: #737373; margin: 4px 0 0; line-height: 1.4;
}

/* Input */
.cp-input-area {
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px; padding: 16px; margin-bottom: 20px;
}
.cp-textarea {
  width: 100%; background: transparent; border: none; color: #e5e5e5;
  font-size: 14px; line-height: 1.6; resize: none; outline: none;
  font-family: inherit;
}
.cp-textarea::placeholder { color: #525252; }
.cp-analyze-btn {
  display: flex; align-items: center; gap: 8px;
  margin-top: 12px; padding: 10px 20px; border: none; border-radius: 10px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: white; font-weight: 600; font-size: 14px; cursor: pointer;
  transition: opacity 0.2s;
}
.cp-analyze-btn:hover { opacity: 0.9; }
.cp-analyze-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* ── P85 Starter chips ─────────────────────────────────────────────── */
.cp-starters { margin-top: 14px; display: flex; flex-direction: column; gap: 8px; }
.cp-starters-label {
  font-size: 10px; font-weight: 800; letter-spacing: 0.14em;
  color: #64748b; text-transform: uppercase; padding: 0 2px;
}
.cp-starters-row {
  display: flex; flex-wrap: wrap; gap: 6px;
}
.cp-starter-chip {
  padding: 7px 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 999px;
  color: #cbd5e1;
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  transition: all 160ms cubic-bezier(0.16,1,0.3,1);
}
.cp-starter-chip:hover {
  background: rgba(34,211,238,0.08);
  border-color: rgba(34,211,238,0.4);
  color: #67e8f9;
  transform: translateY(-1px);
}
.cp-spin { animation: cp-spin 1s linear infinite; }
@keyframes cp-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

.cp-error {
  display: flex; align-items: center; gap: 8px; padding: 12px 16px;
  background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2);
  border-radius: 10px; color: #fca5a5; font-size: 13px; margin-bottom: 16px;
}

/* Results */
.cp-results { display: flex; flex-direction: column; gap: 16px; }

/* P77: Safer Discipline Prediction */
.cp-safer {
  border: 1.5px solid;
  border-radius: 14px;
  padding: 16px 18px;
  display: flex; flex-direction: column; gap: 14px;
}
.cp-safer-head { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }
.cp-safer-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 12px; border: 1px solid; border-radius: 99px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.06em;
}
.cp-safer-stage {
  font-size: 11px; font-weight: 700; color: #cbd5e1;
  padding: 5px 10px; border-radius: 99px;
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
}
.cp-safer-confidence {
  margin-left: auto; font-size: 10px; font-weight: 800;
  letter-spacing: 0.08em; padding: 4px 10px;
  border-radius: 99px; border: 1px solid;
}
.cp-safer-headline {
  margin: 0; font-size: 14px; line-height: 1.5; color: #fafafa; font-weight: 500;
}
.cp-safer-signals {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  font-size: 11px; color: #fca5a5;
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.30);
  border-radius: 8px; padding: 8px 10px;
}
.cp-safer-signals code {
  background: rgba(239,68,68,0.22); color: #fef2f2;
  padding: 2px 7px; border-radius: 5px;
  font-size: 10.5px; font-family: ui-monospace, monospace;
}
.cp-safer-ask {
  background: rgba(56,189,248,0.06);
  border: 1px solid rgba(56,189,248,0.25);
  border-radius: 10px; padding: 10px 14px;
}
.cp-safer-ask-head {
  display: flex; align-items: center; gap: 6px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.10em;
  color: #7dd3fc; margin-bottom: 6px;
}
.cp-safer-ask ul {
  margin: 0; padding-left: 18px; font-size: 12.5px;
  color: #e0f2fe; line-height: 1.55;
  display: flex; flex-direction: column; gap: 4px;
}
.cp-safer-grid { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 10px; }
@media (max-width: 820px) { .cp-safer-grid { grid-template-columns: 1fr; } }
.cp-safer-col {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px; padding: 10px 12px;
}
.cp-safer-col-head {
  display: flex; align-items: center; gap: 6px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.08em;
  color: #94a3b8; margin-bottom: 7px;
}
.cp-safer-col ul {
  margin: 0; padding-left: 16px;
  font-size: 12px; color: #e5e7eb; line-height: 1.5;
  display: flex; flex-direction: column; gap: 4px;
}
.cp-safer-col p {
  margin: 0; font-size: 12.5px; color: #e5e7eb; line-height: 1.55;
}
.cp-safer-empty { font-style: italic; color: #94a3b8; font-size: 11.5px; margin: 0; }
.cp-safer-mit {
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px; padding: 10px 14px;
}
.cp-safer-mit-head {
  display: flex; align-items: center; gap: 6px;
  font-size: 10px; font-weight: 800; letter-spacing: 0.10em;
  color: #cbd5e1; margin-bottom: 8px;
}
.cp-safer-mit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 720px) { .cp-safer-mit-grid { grid-template-columns: 1fr; } }
.cp-safer-mit-col-head {
  display: flex; align-items: center; gap: 6px;
  font-size: 10px; font-weight: 700; color: #cbd5e1; margin-bottom: 6px;
}
.cp-safer-mit ul {
  margin: 0; padding-left: 16px;
  font-size: 12px; color: #e5e7eb; line-height: 1.55;
  display: flex; flex-direction: column; gap: 3px;
}

/* Top row */
.cp-top-row {
  display: flex; align-items: center; gap: 24px;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px; padding: 20px 24px;
}
.cp-gauge { text-align: center; flex-shrink: 0; }
.cp-gauge-score { font-size: 36px; font-weight: 800; margin-top: -8px; letter-spacing: -0.04em; }
.cp-gauge-label { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; }
.cp-outcome-panel { flex: 1; }
.cp-outcome-title { font-size: 18px; font-weight: 700; color: #fafafa; margin: 0 0 8px; }
.cp-outcome-desc { font-size: 13px; color: #a3a3a3; line-height: 1.6; margin: 0; }

/* Action box */
.cp-action-box {
  background: rgba(59,130,246,0.04); border: 1px solid rgba(59,130,246,0.15);
  border-radius: 12px; padding: 16px; overflow: hidden;
}
.cp-action-header {
  display: flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
  color: #60a5fa; margin-bottom: 10px; text-transform: uppercase;
}
.cp-action-box p { font-size: 13px; color: #d4d4d4; line-height: 1.7; margin: 0; }

/* Score breakdown */
.cp-breakdown {
  background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px; padding: 12px 16px;
}
.cp-bd-item {
  display: flex; justify-content: space-between; padding: 6px 0;
  font-size: 13px; color: #a3a3a3;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}
.cp-bd-item:last-child { border-bottom: none; }
.cp-bd-item span { display: flex; align-items: center; gap: 6px; }
.cp-bd-agg span:last-child { color: #f87171; }
.cp-bd-mit span:last-child { color: #4ade80; }
.cp-bd-int span:last-child { color: #facc15; }
.cp-bd-total { font-weight: 700; color: #fafafa; }
.cp-bd-total span:last-child { font-size: 16px; }

/* Factors */
.cp-factors { display: flex; flex-wrap: wrap; gap: 8px; }
.cp-factor {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 600;
}
.cp-factor-agg { background: rgba(239,68,68,0.08); border: 1px solid rgba(239,68,68,0.2); color: #fca5a5; }
.cp-factor-mit { background: rgba(34,197,94,0.08); border: 1px solid rgba(34,197,94,0.2); color: #86efac; }

/* Sections */
.cp-section {
  background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px; overflow: hidden;
}
.cp-section-head {
  display: flex; align-items: center; gap: 10px; width: 100%;
  padding: 12px 16px; background: transparent; border: none;
  color: #d4d4d4; cursor: pointer; font-size: 13px;
}
.cp-section-head:hover { background: rgba(255,255,255,0.02); }
.cp-section-icon {
  width: 28px; height: 28px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.cp-section-title { flex: 1; text-align: left; font-weight: 700; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; }
.cp-section-body { padding: 0 16px 16px; display: flex; flex-direction: column; gap: 10px; }

/* Rule cards */
.cp-rule-card {
  background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px; padding: 12px;
}
.cp-rule-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; }
.cp-rule-issue { font-size: 14px; font-weight: 600; color: #fafafa; }
.cp-rule-weight { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; padding: 2px 8px; border-radius: 4px; background: rgba(255,255,255,0.05); }
.cp-rule-meta { display: flex; gap: 12px; font-size: 11px; color: #737373; margin-bottom: 6px; }
.cp-rule-outcome { font-size: 13px; color: #d4d4d4; font-style: italic; }
.cp-rule-keywords { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 8px; }
.cp-kw-tag {
  font-size: 10px; padding: 2px 8px; border-radius: 4px;
  background: rgba(139,92,246,0.1); border: 1px solid rgba(139,92,246,0.2);
  color: #c4b5fd;
}

/* Match type badges */
.cp-match-badge {
  font-size: 9px; font-weight: 700; letter-spacing: 0.06em;
  padding: 2px 7px; border-radius: 4px; text-transform: uppercase;
}
.cp-match-keyword { background: rgba(59,130,246,0.12); border: 1px solid rgba(59,130,246,0.25); color: #93c5fd; }
.cp-match-semantic { background: rgba(168,85,247,0.12); border: 1px solid rgba(168,85,247,0.25); color: #d8b4fe; }
.cp-bd-sem span:last-child { color: #d8b4fe; }

/* Scenario cards */
.cp-scenario-card {
  background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px; padding: 12px;
}
.cp-scenario-header { display: flex; justify-content: space-between; margin-bottom: 8px; }
.cp-scenario-cat { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: #737373; }
.cp-scenario-risk { font-size: 10px; font-weight: 700; letter-spacing: 0.06em; }
.cp-scenario-text { font-size: 13px; color: #a3a3a3; margin: 0 0 8px; line-height: 1.5; font-style: italic; }
.cp-scenario-answer { font-size: 13px; color: #d4d4d4; line-height: 1.6; }

/* Policy cards */
.cp-policy-card {
  background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px; padding: 12px;
}
.cp-policy-title { font-size: 14px; font-weight: 600; color: #fafafa; margin-bottom: 6px; }
.cp-policy-summary { font-size: 13px; color: #a3a3a3; line-height: 1.6; margin: 0 0 8px; }
.cp-policy-source { font-size: 11px; color: #525252; font-style: italic; }

/* Disclaimer */
.cp-disclaimer {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 14px 16px; border-radius: 10px;
  background: rgba(234,179,8,0.04); border: 1px solid rgba(234,179,8,0.15);
  font-size: 12px; color: #a3a3a3; line-height: 1.5;
}
.cp-disclaimer svg { color: #eab308; flex-shrink: 0; margin-top: 1px; }

@media (max-width: 640px) {
  .cp-top-row { flex-direction: column; text-align: center; }
  .cp-outcome-panel { text-align: center; }
}
      `}</style>
    </div>
  );
}
